import { cn } from '@/lib/utils';
import React, { useState, useEffect, useCallback } from 'react';
import { TextInput } from './text-input';
import { ArrowLeftIcon, ArrowRightIcon } from '@/components/agent-dashboard/assets';
import { IconButton } from './icon-button';
import { TextButton } from './text-button';

/**
 * 分页器组件的属性接口定义
 */
interface PaginationProps {
  /**
   * 当前页码
   * @default 1
   */
  page: number;
  /**
   * 每页显示的条目数量
   * @default 10
   */
  pageSize: number;
  /**
   * 总条目数量
   * @required
   */
  total: number;
  /**
   * 显示在分页器左侧的内容，默认为"{total} items"
   */
  label?: React.ReactNode;
  /**
   * 最多显示的页码数量
   * @default 5
   */
  maxVisiblePages?: number;
  /**
   * 页码改变时的回调函数
   * @param page - 新的页码
   */
  onChange: (page: number) => void;
}

/**
 * 生成页码范围的工具函数
 * @param currentPage - 当前页码
 * @param totalPages - 总页数
 * @param maxVisiblePages - 最多显示的页码数量
 * @returns 页码范围数组，包含数字和省略符号
 */
const generatePageNumbers = (
  currentPage: number,
  totalPages: number,
  maxVisiblePages: number = 5
): (number | 'ellipsis')[] => {
  // 总页数小于等于最大可见页数，直接返回所有页码
  if (totalPages <= maxVisiblePages) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [];
  const half = Math.floor(maxVisiblePages / 2);

  // 添加第一页
  pages.push(1);

  // 当前页靠近开始位置
  if (currentPage <= half + 1) {
    for (let i = 2; i <= maxVisiblePages; i++) {
      pages.push(i);
    }
    pages.push('ellipsis');
    pages.push(totalPages);
  }
  // 当前页靠近结束位置
  else if (currentPage >= totalPages - half) {
    pages.push('ellipsis');
    for (let i = totalPages - maxVisiblePages + 1; i <= totalPages; i++) {
      pages.push(i);
    }
  }
  // 当前页在中间位置
  else {
    pages.push('ellipsis');
    for (let i = currentPage - half; i <= currentPage + half; i++) {
      pages.push(i);
    }
    pages.push('ellipsis');
    pages.push(totalPages);
  }

  return pages;
};

/**
 * 分页器组件
 * 提供页码导航功能，支持上一页、下一页、直接跳转等操作
 */
export const Pagination: React.FC<PaginationProps> = ({
  page: initialPage,
  pageSize = 10,
  total,
  onChange,
  label,
  maxVisiblePages = 5,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  // 标记是否是由回车事件触发的失焦
  const isEnterTriggeredBlurRef = React.useRef(false);
  // 页码框的值
  const [inputPage, setInputPage] = useState(initialPage?.toString());
  // 计算总页数，确保至少为 1
  const totalPages = Math.max(1, Math.ceil((total || 0) / (pageSize || 10)));
  // 当前页码状态，确保在有效范围内
  const [currentPage, setCurrentPage] = useState(
    Math.max(1, Math.min(initialPage, totalPages))
  );

  // 当外部传入的page变化时，更新内部状态
  useEffect(() => {
    const validPage = Math.max(1, Math.min(initialPage, totalPages));
    setCurrentPage(validPage);
    setInputPage(validPage.toString());
  }, [initialPage, totalPages]);

  // 处理页码改变
  const handlePageChange = useCallback((newPage: number) => {
    // 输入页码大于等于最大页数，设置为最大页数
    if (newPage >= totalPages) {
      newPage = totalPages;
    }
    setCurrentPage(newPage);
    setInputPage(newPage.toString());
    onChange(newPage);
  }, [totalPages, onChange]);

  // 处理上一页点击
  const handlePrevious = () => {
    handlePageChange(currentPage - 1);
  };

  // 处理下一页点击
  const handleNext = () => {
    handlePageChange(currentPage + 1);
  };

  // 处理页码输入框变化
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // 只允许输入数字
    const value = e.target.value.replace(/[^0-9]/g, '');
    setInputPage(value);
  };

  // 处理页码输入框失去焦点
  const handleBlur = () => {
    // 如果是由回车事件触发的失焦，则不处理
    if (isEnterTriggeredBlurRef.current) {
      isEnterTriggeredBlurRef.current = false;
      return;
    }

    const page = parseInt(inputPage, 10) || 1;
    handlePageChange(page);
  };

  // 处理页码输入框回车事件
  const handleInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const page = parseInt(inputPage, 10) || 1;
      handlePageChange(page);

      // 设置标志位，表示即将触发的失焦是由回车事件引起的
      isEnterTriggeredBlurRef.current = true;
      inputRef.current?.blur();
    }
  };

  // 生成页码列表
  const pageNumbers = generatePageNumbers(currentPage, totalPages, maxVisiblePages);

  return (
    <div className="flex items-center justify-between  bg-white rounded-lg transition-all select-none">
      {/* 总条目数 */}
      {label ?? (
        <div className="text-sm text-[rgba(52,64,84,1)] mr-4">
          <span className="font-semibold text-[SemiBold] pr-1">{total}</span>
          <span>items</span>
        </div>
      )}

      {/* 分页控制区 */}
      <div className="flex items-center space-x-1">
        {/* 上一页按钮 */}
        <button
          onClick={handlePrevious}
          disabled={currentPage === 1}
          className={cn(
            'flex items-center justify-center w-[110px] h-8 rounded-sm border border-[rgba(208,213,221,1)] text-sm shadow-sm bg-white transition-all',
            currentPage === 1
              ? 'border-bg[rgba(102,112,133,1)] text-gray-400 cursor-not-allowed'
              : 'text-[rgba(52,64,84,1)] hover:border-[rgba(102,112,133,1)]'
          )}
        >
          <ArrowLeftIcon className="mr-1" />
          <span>Previous</span>
        </button>

        {/* 页码列表 */}
        <div className='!mx-3 flex items-center gap-0.5'>
          {pageNumbers.map((pageNum, index) => (
            <React.Fragment key={index}>
              {pageNum === 'ellipsis' ? (
                <span className="px-2 text-[rgba(102,112,133,1)]">...</span>
              ) : (
                <button
                  onClick={() => handlePageChange(pageNum)}
                  className={cn(
                    'size-10 flex items-center justify-center rounded-md text-sm',
                    currentPage === pageNum
                      ? 'bg-[rgba(249,250,251,1)] text-[rgba(16,24,40,1)] border-primary'
                      : 'bg-white text-[rgba(102,112,133,1)]  hover:bg-gray-50'
                  )}
                >
                  {pageNum}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* 下一页按钮 */}
        <button
          onClick={handleNext}
          disabled={currentPage === totalPages}
          className={cn(
            'flex items-center justify-center w-[84px] h-8 rounded-sm border border-[rgba(208,213,221,1)] text-sm  shadow-sm bg-white transition-all',
            currentPage === totalPages
              ? 'border-bg[rgba(102,112,133,1)] text-gray-400 cursor-not-allowed'
              : 'text-[rgba(52,64,84,1)] hover:border-[rgba(102,112,133,1)]'
          )}
        >
          <span>Next</span>
          <ArrowRightIcon className="ml-1" />
        </button>

        {/* 跳转输入框 */}
        <span className="text-[rgba(52,64,84,1)] text-sm font-medium pl-3 pr-2.5">Go to</span>
        <div className='min-w-12'>
          <TextInput
            type="text"
            ref={inputRef}
            value={inputPage}
            onChange={handleInputChange}
            onBlur={handleBlur}
            onKeyDown={handleInputKeyPress}
            className="w-12 h-8 mx-1 px-2 py-1 border rounded-sm text-sm text-center"
            aria-label="Page number"
          />
        </div>
        <span className="text-[rgba(52,64,84,1)] font-medium text-sm">Page</span>
      </div>
    </div>
  );
};

Pagination.displayName = 'Pagination';
